import React, { useContext, useState, useEffect } from "react"
import { Navigate, Outlet, Route, Routes, useNavigate } from "react-router-dom"
import secureLocalStorage from "react-secure-storage"
// context and hooks imports
import { CommonFunctionCtx } from "./context/commonFunctionContext"
import { NotificationCtx } from "./context/notificationContext"
import { SubscriptionCtx } from "./context/subscriptionContext"
import { useAuthenticatedUser } from "./hooks/useAuthenticatedUser"
import { UserCtx } from "./context/userContext"

import { GuideRequests } from "./api/app.service"

// 3rd party imports
import { selectIsConnectedToRoom, useHMSStore } from "@100mslive/react-sdk"
import useWebSocket, { ReadyState } from "react-use-websocket"

// Registration/Sign in component imports
import ClientSignIn from "./components/auth/sign-in/ClientSignIn"
// import Registration from "./components/auth/register/RegistrationPage"

// marketing site component imports
import ContactPage from "./pages/(marketing)/contact/page"
import CookiePolicyPage from "./pages/(marketing)/cookie-policy/page"
import FeaturesPage from "./pages/(marketing)/features/page"
import HomePage from "./pages/(marketing)/page"
import PricingPage from "./components/PricingPage/page"
import PrivacyPolicyPage from "./pages/(marketing)/privacy-policy/page"
import TermsAndConditionsPage from "./pages/(marketing)/terms-and-conditions/page"
import WhyZoeePage from "./pages/(marketing)/why-zoee/page"
import MarketingLayout from "./pages/(marketing)/layout"
import AffiliateProgramTermsPage from "./pages/(marketing)/affiliate-program-terms/page"

import AccountSettingsPage from "./pages/(app)/account-settings/page"
import AvailabilityPage from "./components/scheduling/availability/AvailibilityPage"
import CalendarSettingsPage from "./pages/(app)/account-settings/calendars/page"
import LoginSecurityPage from "./pages/(app)/account-settings/login-security/page"
import NotificationsPage from "./pages/(app)/account-settings/notifications/page"
import PaymentCenterPage from "./pages/(app)/account-settings/payment-center/page"
import PersonalInfoPage from "./pages/(app)/account-settings/personal-info/page"

// List of index, will reorganize when frontend refactoring is complete.
import * as SubscriptionComponents from "./components/subscription"

import * as Client from "./components/client"

import * as Affiliate from "./components/affiliate"

// private route imports
import PrivateRouteCoach from "./components/privateRouteCoach"
import PrivateRouteContact from "./components/privateRouteContact"
import PrivateRouteEmailVerified from "./components/privateRouteEmailVerified"
import PrivateRouteStorageAvailable from "./components/privateRouteStorageAvailable"
import PrivateRouteUser from "./components/privateRouteUser"

import { BBHC20visibilityBanner } from "./components/app-navigation/BBHC20visibilityBanner"
import ClientPayments from "./pages/manage-business/ClientPayments"
import CoachAuthedNav from "./components/navigation/CoachAuthedNav"
import { CoachCard } from "./models/public.interface"
import CoachContacts from "./pages/contacts/CoachContacts"
import CoachDashboard from "./pages/dashboard/CoachDashboard/index"
import CoachInbox from "./pages/inbox/CoachInbox"
import CoachPrivateProfile from "./pages/manage-business/profile/CoachPrivateProfile"
import CoachScheduling from "./pages/scheduling/CoachScheduling"
import ContactServicesFlow from "./components/contacts/contact-profile/ContactServicesFlow"
import DynamicContactProfile from "./components/contacts/contact-profile/DynamicContactProfile"
import ManageBusiness from "./pages/manage-business/ManageBusiness"
import MemberContacts from "./pages/contacts/MemberContacts"

import MigrateToPayPalBanner from "./components/app-navigation/MigrateToPayPalBanner"
import NotesPDFPreview from "./components/notes/NotesPDFPreview"
import NotificationAlert from "./components/alerts/NotificationAlert"
import Notifications from "./pages/notifications/page"
import PopupNotification from "./components/alerts/PopupNotification"
import PrivateRouteSubscription from "./components/privateRouteSubscription"
import ProgramCheckout from "./components/services/ProgramCheckout"
import Services from "./pages/manage-business/Services"
import SessionDraggable from "./components/sessions/SessionDraggable"
import Sessions from "./pages/sessions/Sessions"
import SessionsRouter from "./pages/sessions/SessionsRouter"
import SubscriptionEndingBanner from "./components/app-navigation/SubscriptionEndingBanner"
import { FindACoachPage } from "./pages/find-a-coach/page"
import { QuickSession } from "./pages/sessions/QuickSession"
import TestingLayout from "./pages/testing/layout"
import ConnectPayPalAlert from "./components/subscription/ConnectPayPalAlert"
import Tracker from "./pages/LeadDyno/tracker"
import IAPRCPage from "./pages/iaprc/page"
import ClientManualPage from "./pages/client-manual/page"
import IcfLayout from "./pages/icf/layout"
import IcfRegistrationPage from "./pages/icf/page"
import ICFSubscriptionPage from "./pages/icf/registration/page"
import ICFSubscriptionCheckout from "./pages/icf/subscription/page"
import ConfirmEmailPage from "./pages/confirm-email/page"
import ResetPasswordPage from "./pages/reset-password/page"
import GeneralSignInPage from "./pages/sign-in/page"
import FindACoachLayout from "./pages/find-a-coach/layout"
import CoachPublicProfilePage from "./pages/find-a-coach/[endpoint_slug]/page"
import PublicServiceDetailsPage from "./pages/find-a-coach/[endpoint_slug]/services/[service_id]/page"
import FullBlogPostPage from "./pages/find-a-coach/[endpoint_slug]/blog/[post_id]/page"
import IINPage from "./pages/business-programs/IIN/page"
import EnrollmentFinalStep from "./pages/business-programs/IIN/success/page"
import WrappedIINRegistrationPage from "./pages/business-programs/IIN/iin-registration/page"
import ReferralRedirect from "./components/affiliate/ReferralRedirect"
import useAffiliateTracking from "./components/affiliate/useAffiliateTracking"
import PublicServicePage from "./components/PublicServicePages/PublicServicePage"
import NotFoundPage from "./components/NotFoundPage"
import BusinessBootcampPage from "./pages/business-programs/businessbootcamp/page"
import BusinessBootcampRegistrationPage from "./pages/business-programs/businessbootcamp/BusinessBootcampRegistrationPage"
import BusinessBootcampSuccessPage from "./pages/business-programs/businessbootcamp/BusinessBootcampSuccessPage"
import UpdatePaymentPage from "./pages/(app)/account-settings/payment-center/UpdatePaymentPage"

import OverdueBanner from "./components/Banner/OverdueBanner"

// storyblok blog imports
import BlogRoute from "./components/storyblok/BlogRoute"
import BlogPage from "./pages/(marketing)/blog/page"
import BlogPostPage from "./components/storyblok/BlogPostPage"
import ConciergePage from "./components/storyblok/concierge/ConciergePage"
import PayNowPage from "./pages/(app)/account-settings/payment-center/PayNowPage"

export default function App() {
  const { user, hasOverduePayment } = useContext(UserCtx)
  const navigate = useNavigate()
  const { updateNextStepsData, popupNotification, endActiveSession } =
    useContext(CommonFunctionCtx)
  const { setNotificationAlert, notificationAlert, setLastWebsocketMessage } =
    useContext(NotificationCtx)
  const { activeSubscriptionType, activeSubscription } =
    useContext(SubscriptionCtx)

  const coach: CoachCard = {
    coach_public_profile_id: 0,
    coach_profile_id: 0,
    user_id: 0,
    updated_at: "",
    endpoint_slug: "",
    first_name: "",
    last_name: "",
    description: "",
    tags: [],
    subscription_promo_codes: "",
    max_end_date: "",
    avatar_url: "",
    email: "",
  }

  useEffect(() => {
    if (!authenticatedUser) {
      secureLocalStorage.removeItem("user")
    }
  })

  const [, setShowContactProfile] = useState<boolean>(false)
  const [activeContactProfile, setActiveContactProfile] = useState<any>(null)
  const isConnected = useHMSStore(selectIsConnectedToRoom)
  const [showDraggable, setShowDraggable] = useState<boolean>(false)
  const history = useNavigate()
  const [, setPhoneVerified] = useState<boolean>(user?.phoneVerified)
  const [meetingDetails, setMeetingDetails] = useState<any>(null)
  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [connectPayPalRequest, setConnectPayPalRequest] =
    useState<boolean>(false)
  const [coaches, setCoaches] = useState<any[]>([])
  const [guideOpened, setGuideOpened] = useState<boolean>()
  const authenticatedUser = useAuthenticatedUser(user)

  const handleSendCoaches = (coaches: any[]) => {
    setCoaches(coaches)
  }

  // LEADDYNO ROUTE ALLOWANCE
  useAffiliateTracking()

  // **********
  // WEBSOCKETS
  // **********
  const wsUrl = process.env.REACT_APP_WEBSOCKET_URL || ""
  const [, wsSetMessageHistory] = useState<any[]>([])
  const { sendJsonMessage, lastMessage, readyState } = useWebSocket(wsUrl, {
    onOpen: () => wsSendUserDetails(),
    // Will attempt to reconnect on all close events, such as server shutting down
    shouldReconnect: () => true,
  })

  const connectionStatus = {
    [ReadyState.CONNECTING]: "Connecting",
    [ReadyState.OPEN]: "Open",
    [ReadyState.CLOSING]: "Closing",
    [ReadyState.CLOSED]: "Closed",
    [ReadyState.UNINSTANTIATED]: "Uninstantiated",
  }[readyState]

  const wsSendUserDetails = () => {
    if (!authenticatedUser) {
      console.log("websocket: no user")
    } else {
      const payload = {
        action: "websocketGetConnectionDetails",
        user_id: user.user_id,
      }
      sendJsonMessage(payload)
    }
  }

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "logout") {
        secureLocalStorage.removeItem("user")
        navigate("/sign-in")
      }
    }

    window.addEventListener("storage", handleStorageChange)

    return () => {
      window.removeEventListener("storage", handleStorageChange)
    }
  }, [])

  const parseWebsocketNotificationMessage = (event: MessageEvent<any>) => {
    const parsedObj = JSON.parse(event.data)

    if (parsedObj.type === "notification") {
      setNotificationAlert({
        message: parsedObj.message,
        show: true,
        link: parsedObj.data?.link,
        profile_type: parsedObj.data?.profile_type || "coach",
      })
    }
  }

  const getGuideValue = () => {
    if (authenticatedUser) {
      GuideRequests.getGuideValue({
        user_id: parseInt(user.user_id),
      })
        .then((data) => {
          setGuideOpened(data.guide_opened)
        })
        .catch((error) => {
          console.error("Error fetching guide value: ", error)
        })
    }
  }

  useEffect(() => {
    getGuideValue()
  }, [])

  useEffect(() => {
    if (lastMessage !== null) {
      setLastWebsocketMessage(JSON.parse(lastMessage.data))
      // THIS IS WHERE INCOMING MESSAGES FROM SERVER SHOULD BE PARSED

      parseWebsocketNotificationMessage(lastMessage)

      wsSetMessageHistory((prev) => prev.concat(lastMessage))
    }
  }, [lastMessage, wsSetMessageHistory])

  useEffect(() => {
    const profileId = user?.isCoach ? "coach" : "client"
    window.location.pathname === `/${profileId}/sessions` ||
    window.location.pathname.includes(`/sessions/`)
      ? setShowDraggable(false)
      : setShowDraggable(true)
  }, [history])

  useEffect(() => {
    wsSendUserDetails()
  }, [user])

  useEffect(() => {
    console.log("ws connectionStatus:", connectionStatus)
  }, [readyState])

  useEffect(() => {
    window.addEventListener("unload", () => updateNextStepsData)
    window.addEventListener("unload", endActiveSession)
    return () => {
      window.removeEventListener("unload", () => updateNextStepsData)
      window.removeEventListener("unload", endActiveSession)
      const created_chat = false
      updateNextStepsData(created_chat)
      endActiveSession()
    }
  }, [])

  return (
    <>
      <Tracker />
      <ConnectPayPalAlert
        connectPayPalRequest={connectPayPalRequest}
        setConnectPayPalRequest={setConnectPayPalRequest}
      />
      {hasOverduePayment && !bannerDismissed && <OverdueBanner />}
      <div className="app bg-background relative md:block">
        {popupNotification.show && (
          <PopupNotification popupNotification={popupNotification} />
        )}

        {notificationAlert.show && (
          <div className="fixed left-1/2 z-50 mx-auto w-full max-w-screen-xl -translate-x-1/2 -translate-y-1/2 transform p-2">
            <NotificationAlert notification={notificationAlert} />
          </div>
        )}

        {/* Draggable session video */}
        {isConnected && showDraggable && (
          <div className="hidden md:block">
            <SessionDraggable />
          </div>
        )}

        {/* {authenticatedUser &&
          user.activeProfile === "coach" &&
          activeSubscriptionType === "trial" &&
          !window.location.pathname
            .toLowerCase()
            .includes("iin-registration") &&
          !window.location.pathname
            .toLowerCase()
            .includes("iinbusinessblueprint") &&
          !new URLSearchParams(window.location.search)
            .get("flow")
            ?.toUpperCase()
            .includes("IIN") && <SubscriptionCtaBanner />} */}
        {authenticatedUser &&
          user.activeProfile === "coach" &&
          activeSubscriptionType === "paid" &&
          !window.location.pathname
            .toLowerCase()
            .includes("iin-registration") &&
          !window.location.pathname
            .toLowerCase()
            .includes("iinbusinessblueprint") &&
          !new URLSearchParams(window.location.search)
            .get("flow")
            ?.toUpperCase()
            .includes("IIN") && <SubscriptionEndingBanner />}

        {authenticatedUser && user?.cohort_status === "active" && (
          <MigrateToPayPalBanner />
        )}

        {authenticatedUser &&
          user?.isCoach &&
          activeSubscription?.subscription_promo_code === "BBHC20" && (
            <BBHC20visibilityBanner />
          )}

        {/* {(!authenticatedUser ||
          (user?.isCoach && activeSubscriptionType === "trial")) && (
          <NonUserSubscriptionCtaBanner />
        )} */}

        <Routes>
          {/* Marketing pages */}
          <Route
            element={
              <MarketingLayout
                guideOpened={guideOpened}
                setGuideOpened={setGuideOpened}
              />
            }
          >
            <Route path="/" element={<HomePage />} />
            <Route path="/why-zoee" element={<WhyZoeePage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/blogs" element={<BlogRoute />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/concierge" element={<ConciergePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route
              path="/terms-and-conditions"
              element={<TermsAndConditionsPage />}
            />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route
              path="/affiliate-program-terms"
              element={<AffiliateProgramTermsPage />}
            />
            <Route path="/404" element={<NotFoundPage />} />

            {/* Catch-all route for affiliate-style URLs like /mvang?afmc=123 */}
            <Route path=":referralPath" element={<ReferralRedirect />} />
            {/* 404 route for unmatched marketing pages */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          <Route path="/iaprc" element={<IAPRCPage />} />
          <Route path="/clientmanual" element={<ClientManualPage />} />

          <Route path="/icf" element={<IcfLayout />}>
            <Route index element={<IcfRegistrationPage />} />
            <Route path="registration" element={<ICFSubscriptionPage />} />
            <Route path="subscription" element={<ICFSubscriptionCheckout />} />
          </Route>

          <Route path="/confirm-email" element={<ConfirmEmailPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/testing/*" element={<TestingLayout />} />

          <Route path="/sign-in" element={<GeneralSignInPage />} />
          <Route path="/5daybusinessbootcamp">
            <Route index element={<BusinessBootcampPage />} />
            <Route path="success" element={<BusinessBootcampSuccessPage />} />
            <Route
              path="registration"
              element={<BusinessBootcampRegistrationPage />}
            />
          </Route>
          <Route
            path="/iinbusinessblueprint"
            element={<Navigate to="/5daybusinessbootcamp" replace />}
          />

          {/* <Route path="/iinbusinessblueprint">
            <Route index element={<IINPage />} />
            <Route path="success" element={<EnrollmentFinalStep />} />
            <Route
              path="iin-registration"
              element={<WrappedIINRegistrationPage />}
            />
          </Route> */}

          <Route
            path=":endpoint_slug/booking/:service_id"
            element={<PublicServicePage />}
          />
          <Route
            path=":endpoint_slug/:service_id"
            element={<PublicServicePage />}
          />
          <Route
            path=":endpoint_slug/checkout/:service_id"
            element={<PublicServicePage />}
          />
          <Route
            path="/find-a-coach/*"
            element={
              <FindACoachLayout
                guideOpened={guideOpened}
                setGuideOpened={setGuideOpened}
              />
            }
          >
            <Route
              path=":endpoint_slug/*"
              element={<CoachPublicProfilePage />}
            />

            <Route
              path=":endpoint_slug/services/:service_id"
              element={<PublicServiceDetailsPage />}
            />
            <Route
              path=":endpoint_slug/blog/:post_id"
              element={<FullBlogPostPage />}
            />
            <Route index element={<FindACoachPage />} />
          </Route>

          {/* TODO: Continue NEXTJS route config */}
          {/* Public Quick Session Routes */}
          {/* <Route path="/sessions/:room_code" element={<QuickSession />} /> */}

          {/* Private Routes */}
          <Route element={<PrivateRouteStorageAvailable />}>
            <Route element={<PrivateRouteEmailVerified />}>
              <Route element={<PrivateRouteUser />}>
                <Route
                  element={
                    <>
                      {user?.isCoach === true ? (
                        <CoachAuthedNav
                          guideOpened={guideOpened}
                          setGuideOpened={setGuideOpened}
                        />
                      ) : (
                        <Client.MemberAuthedNav />
                      )}
                      <Outlet />
                    </>
                  }
                >
                  <Route path="account-settings">
                    <Route
                      index
                      element={
                        <AccountSettingsPage
                          guideOpened={guideOpened}
                          setGuideOpened={setGuideOpened}
                        />
                      }
                    />
                    <Route
                      path="personal-info"
                      element={<PersonalInfoPage />}
                    />
                    <Route
                      path="login-security"
                      element={<LoginSecurityPage />}
                    />
                    <Route path="payment-center">
                      <Route index element={<PaymentCenterPage />} />
                      <Route
                        path="update-payment"
                        element={<UpdatePaymentPage />}
                      />
                      <Route path="paynow" element={<PayNowPage />} />
                    </Route>
                    <Route
                      path="calendars"
                      element={<CalendarSettingsPage />}
                    />
                    <Route path="availability" element={<AvailabilityPage />} />
                    <Route
                      path="availability/:id"
                      element={<AvailabilityPage />}
                    />
                    <Route
                      path="notifications"
                      element={<NotificationsPage />}
                    />
                  </Route>
                  <Route path="notifications" element={<Notifications />} />
                  <Route path="sessions" element={<SessionsRouter />} />
                  <Route path="affiliate" element={<Affiliate.ReferACoach />} />
                  {/* Coach Routes */}

                  <Route path="coach">
                    <Route element={<PrivateRouteCoach />}>
                      <Route index element={<ManageBusiness />} />
                      <Route
                        path="edit-website"
                        element={
                          <CoachPrivateProfile guideOpened={guideOpened} />
                        }
                      />
                      <Route path="services" element={<Services />} />
                      <Route
                        path="client-payments"
                        element={<ClientPayments />}
                      />
                      <Route
                        path="client-payments/:merchantId"
                        element={<ClientPayments />}
                      />
                      <Route path="dashboard" element={<CoachDashboard />} />
                      <Route path="inbox" element={<CoachInbox />} />
                      <Route
                        path="sessions/:meeting_id?"
                        element={<Sessions />}
                      />
                      <Route
                        path=":endpoint_slug/services/:service_id"
                        element={
                          <ProgramCheckout
                            activeContactRef
                            contact
                            meetingDetails={meetingDetails}
                            selectedPlanFromChild
                          />
                        }
                      />
                      <Route path="contacts">
                        <Route
                          index
                          element={
                            <CoachContacts
                              setShowContactProfile={setShowContactProfile}
                              coach={coach}
                              onSendCoaches={handleSendCoaches}
                            />
                          }
                        />
                        <Route element={<PrivateRouteContact />}>
                          <Route
                            path=":contact_id"
                            element={
                              <DynamicContactProfile
                                setActiveContactProfile={
                                  setActiveContactProfile
                                }
                              />
                            }
                          />
                          <Route
                            path=":contact_id/services"
                            element={
                              <ContactServicesFlow
                                contact={activeContactProfile}
                              />
                            }
                          />
                          <Route
                            path=":contact_id/notes"
                            element={<NotesPDFPreview />}
                          />
                        </Route>
                      </Route>
                      <Route
                        path="scheduling"
                        element={<CoachScheduling isSchedulePage />}
                      />
                    </Route>
                    <Route
                      path="subscription"
                      element={<PrivateRouteSubscription />}
                    >
                      <Route
                        path="current"
                        element={<SubscriptionComponents.Subscription />}
                      />
                      <Route
                        path="plans"
                        element={<SubscriptionComponents.SubscriptionPlans />}
                      />
                      <Route
                        path="checkout"
                        element={
                          <SubscriptionComponents.SubscriptionCheckout />
                        }
                      />
                    </Route>
                  </Route>

                  <Route path="member">
                    <Route element={<Client.PrivateRouteMember />}>
                      <Route index element={<Client.MemberDashboard />} />
                      <Route
                        path="inbox"
                        element={
                          <Client.MemberInbox
                            phone={user?.phone}
                            setPhoneVerified={setPhoneVerified}
                          />
                        }
                      />
                      <Route
                        path="sessions/:meeting_id?"
                        element={<Sessions />}
                      />
                      <Route
                        path=":endpoint_slug/services/:service_id"
                        element={
                          <ProgramCheckout
                            activeContactRef
                            contact
                            meetingDetails={meetingDetails}
                            selectedPlanFromChild
                          />
                        }
                      />
                      <Route path="contacts">
                        <Route
                          index
                          element={
                            <MemberContacts
                              setShowContactProfile={setShowContactProfile}
                              coach={coach}
                            />
                          }
                        />
                        <Route element={<PrivateRouteContact />}>
                          <Route
                            path=":contact_id"
                            element={
                              <DynamicContactProfile
                                setActiveContactProfile={
                                  setActiveContactProfile
                                }
                              />
                            }
                          />
                          <Route
                            path=":contact_id/services"
                            element={
                              <ContactServicesFlow
                                contact={activeContactProfile}
                              />
                            }
                          />
                        </Route>
                      </Route>
                      <Route
                        path="scheduling"
                        element={<Client.MemberScheduling isSchedulePage />}
                      />
                    </Route>
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>

          {/* Final catch-all route for any unmatched routes */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </>
  )
}
